// Faith-crisis chip in the resource strip: shows the tension (coloured by
// severity) and opens the two remedies — Pilgrimage (gold) and Reform
// (faith) — so they don't crowd the bar.

import { Button } from '@aod/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@aod/ui/components/ui/dropdown-menu';
import { cn } from '@aod/ui/lib/utils';
import { Coins, Flame, Sun } from 'lucide-react';
import { useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import { crisisScene } from '../../ui/sceneEvents.js';
import { sceneUrls } from '../../ui/scenes.js';

interface FaithCrisisMenuProps {
  readonly tension: number;
  readonly canPilgrimage: boolean;
  readonly canReform: boolean;
  readonly onPilgrimage: () => void;
  readonly onReform: () => void;
  /** The crisis events the cycle has fired (the sim's names). */
  readonly firedEvents?: readonly string[];
}

export function crisisTone(tension: number): string {
  if (tension > 80) return 'text-red-600 dark:text-red-400 border-red-500/50 bg-red-500/10';
  if (tension > 60)
    return 'text-orange-600 dark:text-orange-400 border-orange-500/50 bg-orange-500/10';
  if (tension > 40)
    return 'text-yellow-700 dark:text-yellow-400 border-yellow-500/50 bg-yellow-500/10';
  return 'text-muted-foreground border-border';
}

export function FaithCrisisMenu({
  tension,
  canPilgrimage,
  canReform,
  onPilgrimage,
  onReform,
  firedEvents = [],
}: FaithCrisisMenuProps) {
  const t = useT();
  const scene = crisisScene(firedEvents);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid="faith-crisis"
          {...appDom({ id: 'faith-crisis', type: 'Dropdown', role: 'button', scope: 'header' })}
          aria-label={`${t('resources.crisis', { tension })} — ${t('resources.crisisActions')}`}
          className={cn('ml-2 h-7 gap-1 px-2 text-xs font-medium', crisisTone(tension))}
        >
          <Flame className="size-3.5" />
          {/* Icon + number only on phones, so the resource strip fits. */}
          <span className="hidden sm:inline">{t('resources.crisis', { tension })}</span>
          <span className="sm:hidden">{tension}%</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="w-72">
        {scene && (
          <img
            src={sceneUrls(scene.group, scene.id).srcSmall}
            alt=""
            loading="lazy"
            className="rounded-md border border-border/40 object-cover"
            data-testid="faith-crisis-scene"
          />
        )}
        <DropdownMenuLabel>{t('resources.crisisActions')}</DropdownMenuLabel>
        <DropdownMenuItem disabled={!canPilgrimage} onSelect={onPilgrimage}>
          <Coins />
          <span className="flex flex-col">
            <span>{t('resources.pilgrimage')}</span>
            <span className="text-xs text-muted-foreground">{t('resources.pilgrimageTitle')}</span>
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!canReform} onSelect={onReform}>
          <Sun />
          <span className="flex flex-col">
            <span>{t('resources.reform')}</span>
            <span className="text-xs text-muted-foreground">{t('resources.reformTitle')}</span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
